"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/client-api";
import type { EngineSnapshot } from "@/lib/decision-engine-types";

export function useDecisionEngine(active = true) {
  const [snapshot, setSnapshot] = useState<EngineSnapshot | null>(null), [loading, setLoading] = useState(true), [busy, setBusy] = useState<string | null>(null), [error, setError] = useState<string | null>(null);
  const mounted = useRef(true), requestNumber = useRef(0), operationPending = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const refresh = useCallback(async () => {
    const number = ++requestNumber.current;
    try {
      const response = await apiFetch("/api/decision-engine", { cache: "no-store" });
      const data = await response.json() as EngineSnapshot & { error?: string };
      if (!response.ok) throw new Error(data.error || "The decision engine could not be loaded.");
      if (mounted.current && number === requestNumber.current) { setSnapshot(data); setError(null); }
      return data;
    } catch (failure) { if (mounted.current && number === requestNumber.current) setError(failure instanceof Error ? failure.message : "The engine is unavailable."); return null; }
    finally { if (mounted.current) setLoading(false); }
  }, []);
  useEffect(() => { if (active) { const timer = setTimeout(() => void refresh(), 0); return () => clearTimeout(timer); } }, [active, refresh]);
  useEffect(() => {
    const experiment = snapshot?.state.experiment;
    const observing = experiment?.status === "forward" && !experiment.samplingStoppedAt && Date.parse(experiment.samplingUntil || "") > Date.now();
    if (!active || (!busy && snapshot?.state.mandate.status !== "active" && !snapshot?.state.lease && !observing)) return;
    const timer = setInterval(() => void refresh(), busy ? 4_000 : 30_000); return () => clearInterval(timer);
  }, [active, busy, snapshot?.state.mandate.status, snapshot?.state.lease, snapshot?.state.experiment, refresh]);
  const act = useCallback(async (action: string, body: Record<string, unknown> = {}) => {
    if (operationPending.current) return null;
    operationPending.current = true; setBusy(action); setError(null);
    try {
      const response = await apiFetch("/api/decision-engine", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action, ...body }) });
      const data = await response.json() as EngineSnapshot & { error?: string };
      if (!response.ok) throw new Error(data.error || "The engine action failed.");
      ++requestNumber.current;
      if (mounted.current) setSnapshot(data);
      return data;
    } catch (failure) { if (mounted.current) setError(failure instanceof Error ? failure.message : "The engine action failed."); return null; }
    finally { operationPending.current = false; if (mounted.current) setBusy(null); }
  }, []);
  return { snapshot, loading, busy, error, refresh, act };
}
export type DecisionEngineClient = ReturnType<typeof useDecisionEngine>;
export function useEngineParameters() {
  const [search, setSearch] = useState(() => typeof window === "undefined" ? "" : window.location.search);
  useEffect(() => {
    const update = () => setSearch(window.location.search);
    window.addEventListener("hashchange", update); window.addEventListener("popstate", update);
    return () => { window.removeEventListener("hashchange", update); window.removeEventListener("popstate", update); };
  }, []);
  return new URLSearchParams(search);
}
export function engineNavigate(view: "decision" | "autonomy" | "learning" | "simulation", runId?: string, decisionId?: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("engine", view); url.searchParams.delete("chat");
  if (runId) url.searchParams.set("engine_run", runId); else url.searchParams.delete("engine_run");
  if (decisionId) url.searchParams.set("engine_decision", decisionId); else url.searchParams.delete("engine_decision");
  url.hash = view === "decision" ? "agent" : view === "autonomy" ? "portfolio" : view === "learning" ? "world-model" : "simulations";
  window.history.pushState({}, "", url); window.dispatchEvent(new HashChangeEvent("hashchange"));
  window.scrollTo({ top: 0 });
}
export const engineMoney = (micros: number | null) => micros === null || !Number.isFinite(micros) ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(micros / 1_000_000);
export const enginePercent = (p: number | null) => p === null || !Number.isFinite(p) ? "—" : `${Math.round(p * 100) || 0}%`;
export const engineTime = (at: string | null) => at ? new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
